import React, { useState } from 'react';
import styled from 'styled-components';

// Updated title styling for better spacing and consistency
const Title = styled.h1`
  font-size: 3rem;
  text-align: center;
  margin-top: 50px; /* Added margin-top for more space from the top */
  margin-bottom: 50px;
  color: #0070c9; /* Accent color for title */
  font-weight: bold;
`;

const CardWrapper = styled.div`
  background-color: #f9f9f9; /* Light background */
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  padding: 30px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 250px;

  &:hover {
    transform: translateY(-10px); /* Subtle lift effect on hover */
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2); /* Stronger shadow on hover */
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333; /* Darker color for title */
  margin-bottom: 10px;
  transition: color 0.3s ease;

  &:hover {
    color: #0070c9; /* Accent color on hover, similar to Apple's blue */
  }
`;

const CardDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 0;
`;

const ServicesWrapper = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px; /* Slightly larger gap for a clean, spacious feel */
  padding: 60px 40px;
  background-color: #ffffff; /* Clean white background */
  align-items: center;
  justify-items: center;

  @media (max-width: 1024px) {
    padding: 40px 20px;
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 30px 15px;
    gap: 20px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 100px;
  overflow: hidden;
  z-index: 99;
`;

const ModalContent = styled.div`
  background-color: #ffffff;
  padding: 40px;
  border-radius: 12px;
  max-width: 700px;
  width: 90%;
  max-height: 80%; /* Ensure it doesn't take up too much space */
  overflow-y: auto;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.3);
  position: relative;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  z-index: 100;

  @media (max-width: 1024px) {
    max-width: 600px;
    padding: 30px;
  }

  @media (max-width: 768px) {
    max-width: 95%;
    padding: 20px;
  }

  @media (max-width: 480px) {
    max-width: 90%;
    padding: 15px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
  color: #555;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    top: 10px;
    right: 10px;
  }
`;

const ServiceLink = styled.div`
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ServiceDescription = styled.p`
  margin-bottom: 20px;
  font-size: 1.1rem;
  color: #555;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ProjectList = styled.ul`
  margin-top: 30px;
  padding-left: 20px;
`;

const ProjectItem = styled.li`
  margin-bottom: 15px;
  font-size: 1rem;
  color: #444;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      title: "AWS Consulting",
      shortDescription: "Expert AWS advice and cloud optimization.",
      detailedDescription: `
        Our AWS consulting services provide comprehensive solutions to help businesses leverage the full power of AWS. From cloud architecture design to cost optimization and ongoing management, we ensure your AWS infrastructure is efficient, secure, and aligned with your business goals.
        We provide tailored services for organizations of all sizes, ensuring a seamless integration of AWS services, and empowering your team with best practices to achieve long-term success.
      `,
      projects: [
        {
          title: 'Global eCommerce Platform',
          description: `
            We architected and deployed a multi-region, high-availability eCommerce platform for a Fortune 500 retail company. Utilizing services like Amazon ECS, S3, and RDS, the system now supports millions of daily transactions with 99.99% uptime.
            The platform's auto-scaling capabilities allow for dynamic scaling during high-traffic periods, reducing the need for manual intervention. The solution reduced operational costs by 35%, while enhancing customer experience through improved response times.
          `
        },
        {
          title: 'Serverless Financial Infrastructure',
          description: `
            For a leading financial institution, we implemented a fully serverless infrastructure using AWS Lambda, API Gateway, and DynamoDB. This architecture replaced their aging on-premise system, enabling them to handle complex financial transactions in real-time.
            By moving to a serverless model, the company reduced infrastructure management costs by 40%, improved scalability, and maintained compliance with stringent financial regulations such as SOX and PCI-DSS.
          `
        }
      ]
    },
    {
      title: "Azure Consulting",
      shortDescription: "Scalable Azure solutions for your business.",
      detailedDescription: `
        We specialize in Azure consulting, guiding organizations through cloud migrations, data management, and advanced analytics implementations. Our expert team ensures that your Azure infrastructure is scalable, secure, and optimized for performance, providing ongoing support and management for long-term success.
        Whether it’s setting up hybrid cloud architectures or developing advanced AI and machine learning models, we tailor our Azure services to fit your business needs.
      `,
      projects: [
        {
          title: 'Healthcare Cloud Migration',
          description: `
            We led the cloud migration for a healthcare provider, moving their sensitive patient data and core applications to Azure. Using Azure Security Center and Azure Key Vault, we ensured that all patient information remained secure and compliant with HIPAA standards.
            This migration improved data accessibility by 40%, reduced on-premise hardware costs by 50%, and allowed for greater scalability in handling patient records and telemedicine services.
          `
        },
        {
          title: 'IoT-Driven Data Lake',
          description: `
            For a global manufacturing company, we developed an Azure-based IoT data lake. The system integrates real-time data from thousands of IoT sensors across multiple facilities, using Azure Data Lake Storage and Azure Stream Analytics to process the information.
            With this solution, the company now has predictive maintenance insights, reducing equipment downtime by 25% and optimizing production workflows across their supply chain.
          `
        }
      ]
    },
    {
      title: "Google Cloud Consulting",
      shortDescription: "Innovative strategies with Google Cloud.",
      detailedDescription: `
        Our Google Cloud consulting services help businesses harness the full potential of Google Cloud’s cutting-edge technologies. We specialize in cloud-native development, big data analytics, and machine learning, delivering solutions that drive innovation and operational excellence.
        Whether you’re looking to improve your data pipelines, integrate AI into your workflows, or build scalable, cloud-native applications, our Google Cloud experts can guide you every step of the way.
      `,
      projects: [
        {
          title: 'IoT Data Processing for Manufacturing',
          description: `
            We designed and implemented a real-time IoT data processing system for a global manufacturer using Google Cloud Pub/Sub and BigQuery. The system processes data from over 1 million devices in real-time, providing actionable insights on equipment performance and enabling predictive maintenance.
            The solution reduced unplanned downtime by 20% and improved production efficiency through automated reporting and advanced analytics.
          `
        },
        {
          title: 'Media Company Data Pipeline Optimization',
          description: `
            For a leading media company, we optimized their data pipeline by integrating Google Cloud's BigQuery and Cloud Functions. This allowed the company to process large volumes of data faster and more efficiently, reducing query times by 50%.
            With the enhanced data architecture, the media company now generates real-time insights into customer engagement, allowing them to tailor content and advertising strategies to specific audience segments.
          `
        }
      ]
    },
    {
      title: "Cloud Migration",
      shortDescription: "Seamless migration to the cloud.",
      detailedDescription: `
        Our cloud migration services ensure a smooth transition from on-premise environments to cloud-based infrastructures. We handle every aspect of the migration process, from initial assessments to post-migration support, ensuring data integrity and minimal downtime.
        Whether you're migrating applications, data centers, or entire infrastructures, we design cloud solutions that meet your performance, scalability, and security needs, helping you unlock the full potential of cloud computing.
      `,
      projects: [
        {
          title: 'Retail Cloud Migration',
          description: `
            We successfully migrated a retail company’s entire IT infrastructure to AWS, reducing their operational costs by 25% and improving system uptime to 99.9%. Our tailored migration strategy ensured no business disruption during the peak sales season, while also enabling the client to scale their resources based on demand.
          `
        },
        {
          title: 'Tech Startup Cloud Transition',
          description: `
            For a fast-growing tech startup, we transitioned their critical applications and databases from on-premise infrastructure to Google Cloud Platform. This migration cut operational costs by 40%, enhanced deployment speed by 50%, and allowed them to scale their operations in line with rapid user growth.
          `
        }
      ]
    },
    {
      title: "DevOps",
      shortDescription: "Streamline your development processes.",
      detailedDescription: `
        Our DevOps services focus on automating your development and operations workflows for faster delivery and improved reliability. We help organizations build CI/CD pipelines, automate infrastructure with Infrastructure as Code (IaC), and optimize their software delivery processes.
        From initial assessments to full implementation, we help your team achieve agility and efficiency, reducing deployment times and improving collaboration across your development and operations teams.
      `,
      projects: [
        {
          title: 'CI/CD Pipeline Automation',
          description: `
            We implemented fully automated CI/CD pipelines for a logistics firm, using AWS CodePipeline and Terraform to streamline deployments. This automation reduced deployment times from hours to minutes and improved the overall release quality by incorporating automated testing and rollback features, reducing human errors by 30%.
          `
        },
        {
          title: 'Kubernetes Microservices Architecture',
          description: `
            For a global fintech company, we designed a microservices architecture built on Kubernetes, which improved their ability to deploy new features faster. With integrated auto-scaling, the system now handles fluctuating loads more effectively, reducing operational costs by 20% and cutting delivery times for new features by 40%.
          `
        }
      ]
    },
    {
      title: "Security & Compliance",
      shortDescription: "Ensure your cloud is secure and compliant.",
      detailedDescription: `
        Security and compliance are critical in today's cloud-first world. We provide comprehensive security assessments, vulnerability management, and compliance solutions tailored to your industry’s needs.
        Whether you’re dealing with sensitive financial data or health records, we ensure that your cloud environment meets the highest security standards, including PCI-DSS, HIPAA, and GDPR compliance.
      `,
      projects: [
        {
          title: 'Healthcare Security Audit',
          description: `
            We conducted a comprehensive security audit for a multinational healthcare provider, ensuring full HIPAA and GDPR compliance. The project involved deploying advanced encryption protocols and real-time threat detection systems, reducing the risk of data breaches by 80%.
          `
        },
        {
          title: 'Financial Services Security Framework',
          description: `
            For a financial services provider, we deployed a multi-layered security framework using AWS Shield, AWS GuardDuty, and encryption services. The framework provided real-time threat detection, incident response, and compliance with PCI-DSS standards, reducing the client’s vulnerability to cyberattacks by 90%.
          `
        }
      ]
    }
  ];

  const openModal = (service) => {
    setSelectedService(service);
  };

  const closeModal = () => {
    setSelectedService(null);
  };

  return (
    <>
      <Title>Our Services</Title>
      <ServicesWrapper>
        {services.map((service, index) => (
          <ServiceLink key={index} onClick={() => openModal(service)}>
            <CardWrapper>
              <CardTitle>{service.title}</CardTitle>
              <CardDescription>{service.shortDescription}</CardDescription>
            </CardWrapper>
          </ServiceLink>
        ))}
      </ServicesWrapper>

      {selectedService && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>&times;</CloseButton>
            <h2>{selectedService.title}</h2>
            <ServiceDescription>{selectedService.detailedDescription}</ServiceDescription>
            <h3>Sample Projects:</h3>
            <ProjectList>
              {selectedService.projects.map((project, index) => (
                <ProjectItem key={index}>
                  <strong>{project.title}:</strong> {project.description}
                </ProjectItem>
              ))}
            </ProjectList>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default Services;